import { localPartsAt, utcFromLocal, zoneStateAt, type ZoneRules } from '@/lib/time'

/**
 * The time axis.
 *
 * One rule holds the whole thing together: **the window is an interval of UTC
 * instants, and the clock is only a way of labelling it.** Switching between
 * UTC and local therefore relabels the axis without moving a single bar — the
 * failure everybody has seen, where a board jumps seven hours and a rotation
 * appears to change, cannot happen here because nothing about a leg is stored
 * in local time to begin with.
 *
 * UTC is modelled as a zone whose offset is always zero rather than as a
 * special case. Ticks, day bands and snapping then run through exactly one
 * code path, and a bug in that path shows up in both modes instead of hiding
 * in the one nobody uses.
 *
 * Ticks are generated by walking the wall clock, not by adding a fixed number
 * of milliseconds: on the day a zone changes offset, local midnights are 23 or
 * 25 hours apart, and an axis built by repeated addition drifts by an hour for
 * the rest of the window.
 */

/**
 * The frames, each with the run-up it is read with.
 *
 * `lead` is time **before** the frame that stays on screen: an hour for the
 * hour frames, a day for the day frames. A rotation does not begin at midnight
 * — the tail that works the 06:00 out of Hanoi landed there at 23:40 the night
 * before, and a board that starts exactly at the frame edge cuts that arrival
 * off the screen. So "24h" is 23:00 yesterday through midnight tonight, and
 * "7d" is a day before through seven days after.
 */
export const SPANS = [
  { key: '6h', minutes: 360, lead: 60 },
  { key: '12h', minutes: 720, lead: 60 },
  { key: '24h', minutes: 1440, lead: 60 },
  { key: '48h', minutes: 2880, lead: 60 },
  { key: '72h', minutes: 4320, lead: 60 },
  { key: '7d', minutes: 10_080, lead: 1440 },
  { key: '15d', minutes: 21_600, lead: 1440 },
  { key: '1M', minutes: 43_200, lead: 1440 },
] as const

export type SpanKey = (typeof SPANS)[number]['key']

export const DEFAULT_SPAN: SpanKey = '24h'

export function spanMinutes(span: SpanKey): number {
  return SPANS.find((entry) => entry.key === span)?.minutes ?? 1440
}

export function leadMinutes(span: SpanKey): number {
  return SPANS.find((entry) => entry.key === span)?.lead ?? 60
}

/** A zone with no offset and no transitions. UTC as data, not as a branch. */
export const UTC_RULES: ZoneRules = { b: ['UTC', 0, 0], t: [] }

export interface Clock {
  mode: 'utc' | 'local'
  /** IANA zone the axis is labelled in. 'UTC' in UTC mode. */
  zone: string
  rules: ZoneRules
}

export const UTC_CLOCK: Clock = { mode: 'utc', zone: 'UTC', rules: UTC_RULES }

export interface Window {
  startUtc: number
  endUtc: number
}

/** Minute-resolution wall clock arithmetic, done in UTC space then converted. */
function partsToUtc(
  clock: Clock,
  parts: { year: number; month: number; day: number; hour: number; minute: number },
): number {
  return utcFromLocal(clock.zone, clock.rules, parts)
}

/**
 * The window a span covers around an instant.
 *
 * Snapped so the axis reads in round numbers of the clock in force: a 6h frame
 * starts at 00:00, 06:00, 12:00 or 18:00 local, a day frame at local midnight,
 * a month frame on the 1st. The snap uses the display clock, so the frame
 * boundaries follow whichever clock the user is reading.
 */
export function windowFor(anchorUtc: number, span: SpanKey, clock: Clock): Window {
  const startUtc = snapStart(anchorUtc, span, clock)
  return { startUtc, endUtc: frameEnd(startUtc, span, clock) }
}

/**
 * Start of the frame the instant falls in, on the clock in force.
 *
 * Anything a day or longer starts at local midnight of the day it is given —
 * including the month frame, which runs a month forward from the chosen day
 * rather than jumping to the 1st. The day the operator picked is the one thing
 * on this screen that no control is allowed to move.
 */
export function snapStart(anchorUtc: number, span: SpanKey, clock: Clock): number {
  const offset = zoneStateAt(clock.zone, clock.rules, anchorUtc).offsetSeconds
  const parts = localPartsAt(anchorUtc, offset)

  const minutes = spanMinutes(span)
  if (span === '1M' || minutes >= 1440) return partsToUtc(clock, { ...parts, hour: 0, minute: 0 })

  const minuteOfDay = parts.hour * 60 + parts.minute
  const floored = Math.floor(minuteOfDay / minutes) * minutes
  return partsToUtc(clock, { ...parts, hour: Math.floor(floored / 60), minute: floored % 60 })
}

/**
 * Where a frame that starts here ends.
 *
 * Kept separate from the snap so a frame can keep its start across a clock
 * change: switching to UTC must not move the board, only relabel it, and that
 * means the start stays the instant it was while the end is recomputed on the
 * new clock. A day is a local day either way — 23 or 25 hours across a change
 * of offset, never a hard 24.
 */
export function frameEnd(startUtc: number, span: SpanKey, clock: Clock): number {
  if (span === '1M') {
    const offset = zoneStateAt(clock.zone, clock.rules, startUtc).offsetSeconds
    const parts = localPartsAt(startUtc, offset)
    return partsToUtc(clock, { ...parts, month: parts.month + 1 })
  }
  const minutes = spanMinutes(span)
  if (minutes >= 1440) return addLocalDays(startUtc, minutes / 1440, clock)
  return startUtc + minutes * 60_000
}

/**
 * What the board fits to its width: the frame plus its run-up.
 *
 * A day-length run-up is a **local** day, so it is 23 or 25 hours on the day a
 * zone changes offset — the same rule the frame itself follows.
 */
export function viewFor(frame: Window, span: SpanKey, clock: Clock): Window {
  const lead = leadMinutes(span)
  return {
    startUtc:
      lead >= 1440
        ? addLocalDays(frame.startUtc, -(lead / 1440), clock)
        : frame.startUtc - lead * 60_000,
    endUtc: frame.endUtc,
  }
}

/**
 * A frame moved inside the bounds of what has been loaded.
 *
 * Shifted, never shortened. Choosing the 15th and then a month-long frame
 * would run to the 15th of next month, twenty days past everything the
 * operator uploaded; what they want to see is a month **ending** where the
 * data ends, so the window slides back and makes up the difference before the
 * chosen day. The chosen day itself does not move — it stays in the date
 * field, and stays inside the window.
 *
 * A period shorter than the frame is the one case that cannot be satisfied by
 * shifting, and there the window is simply the period.
 */
export function clampFrame(frame: Window, bounds: Window | null): Window {
  if (!bounds) return frame
  const length = frame.endUtc - frame.startUtc
  let { startUtc, endUtc } = frame
  if (endUtc > bounds.endUtc) {
    endUtc = bounds.endUtc
    startUtc = endUtc - length
  }
  if (startUtc < bounds.startUtc) {
    startUtc = bounds.startUtc
    endUtc = Math.min(bounds.endUtc, startUtc + length)
  }
  return { startUtc, endUtc }
}

/** Start of the local day an instant falls in. */
export function startOfLocalDay(utcMs: number, clock: Clock): number {
  return snapStart(utcMs, '24h', clock)
}

/** Same wall-clock time N days later, which is not always N×24h later. */
export function addLocalDays(utcMs: number, days: number, clock: Clock): number {
  const offset = zoneStateAt(clock.zone, clock.rules, utcMs).offsetSeconds
  const parts = localPartsAt(utcMs, offset)
  return partsToUtc(clock, { ...parts, day: parts.day + days })
}

/** The frame before or after this one, snapped the same way. */
export function stepWindow(window: Window, span: SpanKey, clock: Clock, direction: 1 | -1): number {
  if (span === '1M') {
    const offset = zoneStateAt(clock.zone, clock.rules, window.startUtc).offsetSeconds
    const parts = localPartsAt(window.startUtc, offset)
    return partsToUtc(clock, { ...parts, month: parts.month + direction })
  }
  const minutes = spanMinutes(span)
  if (minutes >= 1440) return addLocalDays(window.startUtc, direction * (minutes / 1440), clock)
  return window.startUtc + direction * minutes * 60_000
}

export interface Tick {
  utcMs: number
  /** Fraction of the window, 0..1. */
  position: number
  label: string
  major: boolean
  /** First tick of a local day — the strongest line on the axis. */
  dayStart: boolean
}

export interface DayBand {
  key: string
  start: number
  end: number
  label: string
}

/** Minor and major tick steps in minutes, per span. */
const STEPS: Record<SpanKey, [minor: number, major: number]> = {
  '6h': [30, 60],
  '12h': [60, 120],
  '24h': [60, 180],
  '48h': [120, 360],
  '72h': [180, 720],
  '7d': [360, 1440],
  '15d': [720, 1440],
  '1M': [1440, 1440],
}

/** True when the axis carries dates only and a row of clock labels would be noise. */
export function timeRowHidden(span: SpanKey): boolean {
  return STEPS[span][1] >= 1440
}

export function ticksFor(window: Window, span: SpanKey, clock: Clock): Tick[] {
  const [minor, major] = STEPS[span]
  const total = window.endUtc - window.startUtc
  const ticks: Tick[] = []

  const offset = zoneStateAt(clock.zone, clock.rules, window.startUtc).offsetSeconds
  const parts = localPartsAt(window.startUtc, offset)
  const minuteOfDay = parts.hour * 60 + parts.minute
  let cursor = Math.floor(minuteOfDay / minor) * minor
  let day = { year: parts.year, month: parts.month, day: parts.day }

  // Walks wall-clock minutes, so a day that gains or loses an hour still puts
  // its ticks on the hour.
  for (let guard = 0; guard < 4000; guard += 1) {
    if (cursor >= 1440) {
      cursor -= 1440
      const next = new Date(Date.UTC(day.year, day.month - 1, day.day + 1))
      day = { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: next.getUTCDate() }
    }
    const utcMs = partsToUtc(clock, {
      ...day,
      hour: Math.floor(cursor / 60),
      minute: cursor % 60,
    })
    if (utcMs >= window.endUtc) break
    if (utcMs >= window.startUtc) {
      const isMajor = cursor % major === 0
      ticks.push({
        utcMs,
        position: (utcMs - window.startUtc) / total,
        label: cursor === 0 ? formatDayLabel(day) : formatClock(cursor),
        major: isMajor,
        dayStart: cursor === 0,
      })
    }
    cursor += minor
  }
  return ticks
}

/** Local days the window touches, for the band above the ticks. */
export function dayBandsFor(window: Window, clock: Clock): DayBand[] {
  const bands: DayBand[] = []
  const offset = zoneStateAt(clock.zone, clock.rules, window.startUtc).offsetSeconds
  const parts = localPartsAt(window.startUtc, offset)
  let day = { year: parts.year, month: parts.month, day: parts.day }
  let start = window.startUtc

  for (let guard = 0; guard < 400; guard += 1) {
    const next = new Date(Date.UTC(day.year, day.month - 1, day.day + 1))
    const nextDay = {
      year: next.getUTCFullYear(),
      month: next.getUTCMonth() + 1,
      day: next.getUTCDate(),
    }
    const boundary = partsToUtc(clock, { ...nextDay, hour: 0, minute: 0 })
    const end = Math.min(boundary, window.endUtc)
    bands.push({
      key: `${day.year}-${day.month}-${day.day}`,
      start,
      end,
      label: formatDayLabel(day),
    })
    if (end >= window.endUtc) break
    start = end
    day = nextDay
  }
  return bands
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function formatDayLabel(day: { year: number; month: number; day: number }): string {
  const weekday = WEEKDAYS[new Date(Date.UTC(day.year, day.month - 1, day.day)).getUTCDay()]
  return `${weekday} ${String(day.day).padStart(2, '0')} ${MONTHS[day.month - 1]}`
}

function formatClock(minuteOfDay: number): string {
  const hour = Math.floor(minuteOfDay / 60)
  const minute = minuteOfDay % 60
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/** "08:05" on the clock in force at that instant. */
export function clockAt(utcMs: number, clock: Clock): string {
  const offset = zoneStateAt(clock.zone, clock.rules, utcMs).offsetSeconds
  const parts = localPartsAt(utcMs, offset)
  return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`
}

/** "08:05:31" — for a clock the user watches tick. */
export function clockWithSeconds(utcMs: number, clock: Clock): string {
  const offset = zoneStateAt(clock.zone, clock.rules, utcMs).offsetSeconds
  const parts = localPartsAt(utcMs, offset)
  return `${pad2(parts.hour)}:${pad2(parts.minute)}:${pad2(parts.second)}`
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value)
}

/** "Mon 01 Sep 08:05" — used where a bar label has room for the date too. */
export function stampAt(utcMs: number, clock: Clock): string {
  const offset = zoneStateAt(clock.zone, clock.rules, utcMs).offsetSeconds
  const parts = localPartsAt(utcMs, offset)
  return `${formatDayLabel(parts)} ${clockAt(utcMs, clock)}`
}

/** 328 -> "5:28". Block and delay figures read as durations, never as clocks. */
export function formatDuration(minutes: number): string {
  const sign = minutes < 0 ? '-' : ''
  const total = Math.abs(minutes)
  return `${sign}${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}
